import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { Spinner, Toast, css, PageHeader, Modal, Label } from '../components/UI';
import { useMotivosRechazo, SelectMotivoRechazo } from '../components/MotivoRechazo';

// Bandeja de aprobación de requisiciones: lo que espera la decisión del usuario.
//  · Aprobación del área (al inicio, según las reglas de Administración)
//  · Validación de Finanzas (al final: con las órdenes de compra ya emitidas)
// La lógica y los permisos viven en el backend (utils/aprobacionRequisicion.js).

const fmt = (n) => `$${(Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fecha = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const ESTADO_PASO = { pendiente: 'Pendiente', aprobado: 'Aprobó', rechazado: 'Rechazó' };
const COLOR_PASO = { pendiente: 'var(--st-pendiente)', aprobado: 'var(--ok)', rechazado: 'var(--danger)' };

export default function AprobarRequisiciones() {
  const { user } = useAuth();
  const esAdmin = user?.rol === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [todas, setTodas] = useState(false);
  const [abierta, setAbierta] = useState(null);
  const [decision, setDecision] = useState(null); // { req, accion, comentario, enviando }
  const [reasignar, setReasignar] = useState(null); // { req, usuarios, elegidos, enviando }
  const [toast, setToast] = useState(null);
  const motivosRechazo = useMotivosRechazo();
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4500); };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setItems((await api.get(`/requisiciones/por-aprobar${todas ? '?todas=1' : ''}`)).items || []); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [todas]);
  useEffect(() => { load(); }, [load]);

  const descargar = async (ruta, nombre) => {
    try {
      const blob = await api.downloadPdf(ruta);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: nombre }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast_(e.message, 'error'); }
  };

  const enTurno = (r) => {
    if (r.etapa === 'finanzas') return (r.validacionFinanzas?.pendientesDe || []).map(String).includes(String(user?._id));
    return (r.aprobacionArea?.pendientesDe || []).map(String).includes(String(user?._id));
  };

  const confirmar = async () => {
    const { req: r, accion, comentario, categoria } = decision;
    if (accion === 'rechazar' && !categoria) { toast_('Elige el motivo del rechazo', 'error'); return; }
    if (accion === 'rechazar' && !comentario.trim()) { toast_('Escribe el detalle del rechazo', 'error'); return; }
    setDecision(d => ({ ...d, enviando: true }));
    try {
      const ruta = r.etapa === 'finanzas' ? 'validacion-finanzas' : 'aprobacion';
      await api.post(`/requisiciones/${r._id}/${ruta}`, { accion, comentario, categoria });
      toast_(accion === 'aprobar' ? `${r.folio} aprobada.` : `${r.folio} rechazada.`);
      setDecision(null); setAbierta(null);
      load();
    } catch (e) { toast_(e.message, 'error'); setDecision(d => ({ ...d, enviando: false })); }
  };

  const abrirReasignar = async (r) => {
    try {
      const d = await api.get('/auth/usuarios?limit=500');
      const usuarios = (d.items || d || []).filter(u => u.activo && String(u._id) !== String(r.usuario?._id));
      setReasignar({ req: r, usuarios, elegidos: [], enviando: false });
    } catch (e) { toast_(e.message, 'error'); }
  };
  const confirmarReasignar = async () => {
    if (!reasignar.elegidos.length) { toast_('Elige al menos un aprobador', 'error'); return; }
    setReasignar(x => ({ ...x, enviando: true }));
    try {
      await api.post(`/requisiciones/${reasignar.req._id}/aprobacion/reasignar`, { aprobadores: reasignar.elegidos });
      toast_(`${reasignar.req.folio}: paso reasignado.`);
      setReasignar(null); load();
    } catch (e) { toast_(e.message, 'error'); setReasignar(x => ({ ...x, enviando: false })); }
  };

  const area = items.filter(i => i.etapa === 'area');
  const finanzas = items.filter(i => i.etapa === 'finanzas');

  const Tarjeta = ({ r }) => {
    const pasos = r.aprobacionArea?.pasos || [];
    const iTurno = pasos.findIndex(p => p.estado === 'pendiente');
    const ocs = r.ordenesCompra || [];
    const totalOC = ocs.reduce((s, o) => s + (o.total || 0), 0);
    const abiertaEsta = abierta === r._id;
    const puedeDecidir = enTurno(r) || esAdmin;
    return (
      <div style={{ ...css.card, padding: 0, marginBottom: 12, overflow: 'hidden' }}>
        <button type="button" onClick={() => setAbierta(abiertaEsta ? null : r._id)} aria-expanded={abiertaEsta}
          style={{ width: '100%', textAlign: 'left', border: 0, background: 'transparent', padding: '14px 18px', cursor: 'pointer', font: 'inherit', color: 'inherit', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 12, alignItems: 'center' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--digit-font)', fontSize: 12.5, color: 'var(--plasma)' }}>{r.folio}</span>
              <span style={{ fontSize: 12, color: 'var(--glass)' }}>
                {r.etapa === 'finanzas' ? 'Validación de Finanzas' : `Aprobación del área${pasos.length > 1 ? ` · paso ${iTurno + 1} de ${pasos.length}` : ''}`}
              </span>
              {!enTurno(r) && <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>· en turno de {r.etapa === 'finanzas' ? 'Finanzas' : (pasos[iTurno]?.aprobadores || []).map(a => a.nombre).join(' o ')}</span>}
            </div>
            <div style={{ fontSize: 13.5, marginTop: 4 }}>
              <strong>{r.usuario?.nombre || 'Solicitante'}</strong> · {r.empresa} · {[r.direccion, r.centroCostos].filter(Boolean).join(' · ')}
            </div>
            {r.justificacion && <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: abiertaEsta ? 'normal' : 'nowrap' }}>{r.justificacion}</div>}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: 'var(--digit-font)', fontSize: 16 }}>{fmt(r.etapa === 'finanzas' ? totalOC : r.total)}</div>
            <div style={{ fontSize: 11.5, color: 'var(--glass)' }}>{r.etapa === 'finanzas' ? `${ocs.length} orden(es) de compra` : 'estimado'} · {fecha(r.createdAt)}</div>
          </div>
        </button>

        {abiertaEsta && (
          <div style={{ padding: '4px 18px 16px', borderTop: '1px solid var(--mesh-soft)' }}>
            {r.etapa === 'finanzas' ? (
              <>
                <div className="nx-label" style={{ margin: '12px 0 6px' }}>Órdenes de compra</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr>{['Orden', 'Proveedor', 'Condición', 'Total', ''].map((h, i) => <th key={i} scope="col" style={{ textAlign: i === 3 ? 'right' : 'left', padding: '6px 8px' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {ocs.map(o => (
                      <tr key={o._id} style={{ borderTop: '1px solid var(--mesh-soft)' }}>
                        <td style={{ padding: '7px 8px', fontFamily: 'var(--digit-font)', fontSize: 12 }}>
                          {o.folio}
                          {(o.revision || 1) > 1 && (() => { const u = o.revisiones[o.revisiones.length - 1]; return (
                            <div style={{ fontFamily: 'inherit', fontSize: 11.5, color: 'var(--plasma)', marginTop: 2 }} title={(u.cambios || []).join('\n')}>
                              Rev. {o.revision} · editada por {u.nombreUsuario}: {u.motivo}{Math.abs(u.totalNuevo - u.totalAnterior) > 0.005 ? ` (${fmt(u.totalAnterior)} → ${fmt(u.totalNuevo)})` : ''}
                            </div>); })()}
                        </td>
                        <td style={{ padding: '7px 8px' }}>{o.proveedor}</td>
                        <td style={{ padding: '7px 8px' }}>{o.condicionPago === 'credito' ? 'Crédito' : 'Contado'}</td>
                        <td style={{ padding: '7px 8px', textAlign: 'right', fontFamily: 'var(--digit-font)' }}>{fmt(o.total)}</td>
                        <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                          <button type="button" onClick={() => descargar(`/requisiciones/${r._id}/ordenes-compra/${o._id}/pdf`, `OrdenCompra_${o.folio}.pdf`)} style={{ ...css.btnGhost, fontSize: 12, padding: '4px 10px' }}>PDF</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', marginTop: 8 }}>
                  Estimado al solicitar: <span style={{ fontFamily: 'var(--digit-font)' }}>{fmt(r.total)}</span> · órdenes: <span style={{ fontFamily: 'var(--digit-font)' }}>{fmt(totalOC)}</span>
                </div>
              </>
            ) : (
              <>
                <div className="nx-label" style={{ margin: '12px 0 6px' }}>Partidas</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr>{['Descripción', 'Cantidad', 'Precio', 'Importe'].map((h, i) => <th key={h} scope="col" style={{ textAlign: i ? 'right' : 'left', padding: '6px 8px' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {(r.partidas || []).map((p, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--mesh-soft)' }}>
                        <td style={{ padding: '7px 8px' }}>{p.descripcion}</td>
                        <td style={{ padding: '7px 8px', textAlign: 'right' }}>{p.cantidad} {p.unidad}</td>
                        <td style={{ padding: '7px 8px', textAlign: 'right', fontFamily: 'var(--digit-font)' }}>{fmt(p.precioUnitario)}</td>
                        <td style={{ padding: '7px 8px', textAlign: 'right', fontFamily: 'var(--digit-font)' }}>{fmt(p.importe)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="nx-label" style={{ margin: '14px 0 6px' }}>Cadena de aprobación · {r.aprobacionArea?.regla?.descripcion}</div>
                <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                  {pasos.map((p, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      {p.aprobadores.map(a => a.nombre).join(' o ')}{' '}
                      <span style={{ color: COLOR_PASO[p.estado], fontWeight: 600 }}>· {p.estado === 'pendiente' && i === iTurno ? 'En turno' : ESTADO_PASO[p.estado]}</span>
                      {p.nombreResolvio && <span style={{ color: 'var(--glass)' }}> ({p.nombreResolvio}, {fecha(p.fecha)}{p.comentario ? `: ${p.comentario}` : ''})</span>}
                    </li>
                  ))}
                </ol>
              </>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 14 }}>
              <button type="button" onClick={() => descargar(`/requisiciones/${r._id}/pdf`, `Requisicion_${r.folio}.pdf`)} style={{ ...css.btnGhost, fontSize: 12.5 }}>PDF de la requisición</button>
              {esAdmin && r.etapa === 'area' && <button type="button" onClick={() => abrirReasignar(r)} style={{ ...css.btnGhost, fontSize: 12.5 }}>Reasignar paso</button>}
              {puedeDecidir && <>
                <button type="button" onClick={() => setDecision({ req: r, accion: 'rechazar', comentario: '', categoria: '', enviando: false })} style={{ ...css.btn('peligro'), fontSize: 12.5 }}>Rechazar</button>
                <button type="button" onClick={() => setDecision({ req: r, accion: 'aprobar', comentario: '', enviando: false })} style={{ ...css.btn('primario'), fontSize: 12.5 }}>{r.etapa === 'finanzas' ? 'Validar' : 'Aprobar'}</button>
              </>}
            </div>
          </div>
        )}
      </div>
    );
  };

  const Seccion = ({ titulo, ayuda, lista, vacio }) => (
    <section style={{ marginBottom: 22 }}>
      <h2 style={{ margin: '0 0 4px', fontFamily: 'var(--label-font)', fontSize: 16, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--plasma)' }}>{titulo} · {lista.length}</h2>
      <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--glass)' }}>{ayuda}</p>
      {lista.length ? lista.map(r => <Tarjeta key={r._id} r={r} />) : <div style={{ ...css.card, padding: 16, fontSize: 13, color: 'var(--glass)' }}>{vacio}</div>}
    </section>
  );

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <PageHeader title="Requisiciones por aprobar"
        subtitle="Las que esperan tu decisión. Presiona una para ver su detalle."
        actions={<>
          {esAdmin && <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink-soft)' }}>
            <input type="checkbox" checked={todas} onChange={e => setTodas(e.target.checked)} /> Ver todas las que están en aprobación
          </label>}
          <button type="button" onClick={load} disabled={loading} style={{ ...css.btnGhost, fontSize: 13 }}>Actualizar</button>
        </>} />
      {loading && !items.length && <Spinner />}
      {error && <div role="alert" style={{ ...css.card, padding: 16, color: 'var(--danger)', display: 'flex', justifyContent: 'space-between', gap: 12 }}><span>{error}</span><button type="button" onClick={load} style={css.btnGhost}>Reintentar</button></div>}
      {!loading && !error && (
        <>
          <Seccion titulo="Aprobación del área" lista={area} vacio="No tienes requisiciones por aprobar."
            ayuda="Requisiciones recién solicitadas en tu área. Al aprobar pasan al siguiente aprobador o al jefe de compras." />
          <Seccion titulo="Validación de Finanzas" lista={finanzas} vacio="No hay requisiciones esperando la validación de Finanzas."
            ayuda="Compras ya cotizó y emitió las órdenes de compra. Al validar, se puede generar la solicitud de pago; si rechazas, regresa al comprador." />
        </>
      )}

      <Modal open={!!decision} onClose={() => !decision?.enviando && setDecision(null)} width={480}
        title={decision?.accion === 'rechazar' ? 'Rechazar requisición' : decision?.req?.etapa === 'finanzas' ? 'Validar requisición' : 'Aprobar requisición'}>
        {decision && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}><span style={{ fontFamily: 'var(--digit-font)', color: 'var(--plasma)' }}>{decision.req.folio}</span> · {decision.req.usuario?.nombre} · {decision.req.empresa}</div>
            {decision.accion === 'rechazar' && <SelectMotivoRechazo value={decision.categoria} motivos={motivosRechazo} onChange={v => setDecision(d => ({ ...d, categoria: v }))} />}
            <div>
              <Label required={decision.accion === 'rechazar'}>{decision.accion === 'rechazar' ? 'Detalle del rechazo' : 'Comentario (opcional)'}</Label>
              <textarea rows={3} value={decision.comentario} onChange={e => setDecision(d => ({ ...d, comentario: e.target.value }))} style={{ ...css.input(false), resize: 'vertical' }} />
              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 4 }}>
                {decision.accion === 'rechazar'
                  ? (decision.req.etapa === 'finanzas' ? 'Regresa al comprador con este motivo para recotizar o corregir las órdenes.' : 'Regresa al solicitante con este motivo para corregirla y reenviarla.')
                  : 'Se le avisará por correo al siguiente responsable.'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setDecision(null)} disabled={decision.enviando} style={css.btnGhost}>Cancelar</button>
              <button type="button" onClick={confirmar} disabled={decision.enviando}
                style={{ ...css.btn(decision.accion === 'rechazar' ? 'peligro' : 'primario'), opacity: decision.enviando ? 0.7 : 1 }}>
                {decision.enviando ? 'Guardando…' : decision.accion === 'rechazar' ? 'Rechazar' : decision.req.etapa === 'finanzas' ? 'Validar' : 'Aprobar'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!reasignar} onClose={() => !reasignar?.enviando && setReasignar(null)} title="Reasignar paso" width={460}>
        {reasignar && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>Elige quién aprueba el paso en turno de <span style={{ fontFamily: 'var(--digit-font)', color: 'var(--plasma)' }}>{reasignar.req.folio}</span>. Si eliges varios, basta con que apruebe uno.</div>
            <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--mesh)', borderRadius: 4, padding: 8 }}>
              {reasignar.usuarios.map(u => (
                <label key={u._id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 2px', fontSize: 13 }}>
                  <input type="checkbox" checked={reasignar.elegidos.includes(u._id)}
                    onChange={e => setReasignar(x => ({ ...x, elegidos: e.target.checked ? [...x.elegidos, u._id] : x.elegidos.filter(id => id !== u._id) }))} />
                  {u.nombre} <span style={{ color: 'var(--glass)' }}>· {u.email}</span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setReasignar(null)} disabled={reasignar.enviando} style={css.btnGhost}>Cancelar</button>
              <button type="button" onClick={confirmarReasignar} disabled={reasignar.enviando} style={css.btn('primario')}>Reasignar</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
